import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'fesmelacta_rated';

const Star = ({ filled, hovered, onClick, onMouseEnter, onMouseLeave }) => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill={filled || hovered ? '#f5a623' : 'none'}
        stroke={filled || hovered ? '#f5a623' : '#d4d4d4'}
        strokeWidth="1.5"
        style={{
            cursor: 'pointer',
            transition: 'transform 0.15s, fill 0.15s',
            transform: hovered ? 'scale(1.15)' : 'scale(1)',
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const CloseIcon = ({ onClick }) => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#999"
        strokeWidth="2"
        strokeLinecap="round"
        style={{ cursor: 'pointer', flexShrink: 0 }}
        onClick={onClick}
    >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

function RatingWidget({ language }) {
    const [rating, setRating] = useState(0);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [step, setStep] = useState('stars'); // 'stars' | 'review' | 'done' | 'hidden'
    const [review, setReview] = useState('');
    const [alreadyRated, setAlreadyRated] = useState(false);
    const [visible, setVisible] = useState(false);
    const [savedRow, setSavedRow] = useState(null);

    useEffect(() => {
        if (localStorage.getItem(STORAGE_KEY)) {
            setAlreadyRated(true);
        } else {
            const timer = setTimeout(() => setVisible(true), 800);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleStarClick = async (selectedRating) => {
        setRating(selectedRating);
        setStep('review');
        localStorage.setItem(STORAGE_KEY, 'true');

        try {
            const res = await fetch('/save-rating', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating: selectedRating, language }),
            });
            const data = await res.json();
            if (data.row) setSavedRow(data.row);
        } catch (err) {
            console.error('Error saving rating:', err);
        }
    };

    const handleDismiss = () => {
        setVisible(false);
        setTimeout(() => setStep('hidden'), 300);
    };

    const handleSubmitReview = async () => {
        setStep('done');
        setTimeout(() => {
            setVisible(false);
            setTimeout(() => setStep('hidden'), 300);
        }, 1500);

        if (!review.trim()) return;

        try {
            if (savedRow) {
                await fetch('/update-rating-review', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ row: savedRow, review: review.trim() }),
                });
            }
        } catch (err) {
            console.error('Error updating review:', err);
        }
    };

    if (alreadyRated || step === 'hidden') return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
            width: '300px',
            padding: '16px',
            borderRadius: '12px',
            backgroundColor: '#fff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
            transform: visible ? 'translateY(0)' : 'translateY(calc(100% + 30px))',
            opacity: visible ? 1 : 0,
            transition: 'transform 0.3s ease, opacity 0.3s ease',
        }}>
            {step === 'done' ? (
                <div style={{ textAlign: 'center', padding: '4px 0' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>
                        {rating >= 4 ? '\u2764\uFE0F' : '\uD83D\uDE4F'}
                    </div>
                    <span style={{ fontSize: '14px', color: '#555' }}>Gràcies per la teva valoració!</span>
                </div>
            ) : (
                <>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                    }}>
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                            T'ha resultat útil?
                        </span>
                        <CloseIcon onClick={handleDismiss} />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '4px',
                        marginBottom: step === 'review' ? '12px' : '0',
                    }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                                key={star}
                                filled={star <= rating}
                                hovered={star <= hoveredStar}
                                onClick={() => handleStarClick(star)}
                                onMouseEnter={() => setHoveredStar(star)}
                                onMouseLeave={() => setHoveredStar(0)}
                            />
                        ))}
                    </div>
                    {step === 'review' && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                value={review}
                                onChange={(e) => setReview(e.target.value)}
                                placeholder="Comentari opcional..."
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleSubmitReview()}
                                style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    fontSize: '13px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    backgroundColor: '#fafafa',
                                }}
                            />
                            <button
                                onClick={handleSubmitReview}
                                style={{
                                    padding: '8px 16px',
                                    minHeight: '44px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    backgroundColor: '#2e7d32',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s',
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#1b5e20'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#2e7d32'}
                            >
                                Envia
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default RatingWidget;
